import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useCouponApi } from '../../hooks/useCouponApi';

export const useCreateCoupon = () => {
  const { createCoupon } = useCouponApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationFn: (data: any) => createCoupon(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['creatCoupon'] });
      showMessage(
        response?.message || 'Coupon created successfully',
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['getCoupon'] });
      showMessage(
        response?.message || 'Coupon created successfully',
        'success'
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to create Coupon';
      showMessage(errorMessage, 'error');
    },
  });
};

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};

export const useGetAllCoupon = (queryParams: Params) => {

  const { getAllCoupon } = useCouponApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['getCoupon', queryParams],
    queryFn: () => {
      return getAllCoupon(queryParams);
    },
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch
  };
};

export const UseEditCouponById = () => {
  const { editCouponById } = useCouponApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editCouponById(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['getCoupon'] });
      showMessage(
        response?.message || 'Coupon updated successfully',
        'success'
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update Coupon';
      showMessage(errorMessage, 'error');
    },
  });
};

export const useDeletCouponById = () => {
  const queryClient = useQueryClient();
  const { deleteCouponById } = useCouponApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteCoupon'],
    mutationFn: (ids: string[]) => {
      return deleteCouponById(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Coupon deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['couponDelete'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Coupon',
        'error'
      );
    },
  });
};

export const useBlockUnblockCouponById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockOrUnblockCouponById } = useCouponApi();

  return useMutation({
    mutationKey: ['blockUnblockCoupon'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockOrUnblockCouponById({ id, status: data.status }),

    onSuccess: (_response, _variables) => {
      const status = _variables.data.status;
      const action = status === 'blocked' ? 'blocked' : 'unblocked';
      showMessage(`Coupon ${action} successfully`, 'success');

      queryClient.invalidateQueries({
        queryKey: ['getCoupon'],
        exact: false,
      });
    },

    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
        'Failed to update Coupon status',
        'error'
      );
    },
  });
};

export const useCoupons = () => {
  const { generateCoupon } = useCouponApi();
  return useMutation({
    mutationFn: generateCoupon,
  });
};
